import { RpcFunction } from '@examdojo/supabase';

export type StudentInsightsRpcRow = RpcFunction<'get_student_insights', 'classroom'>['Returns'][number];

export interface StudentInsightsWeakestSpotUIModel {
  topicLevel02Id: number;
  topicCode: string;
  topicName: string;
  markImpact: number;
  avgAccuracy: number;
}

export interface StudentInsightsTopicUIModel {
  topicLevel02Id: number;
  topicCode: string;
  topicName: string;
  topicGroupName: string;
  avgMark: number | null;
  examWeight: number;
  markImpact: number | null;
  avgAccuracy: number | null;
  lower: number | null;
  upper: number | null;
  isAssessed: boolean;
}

export interface StudentInsightsProjectedScoreUIModel {
  weekStartDate: string;
  scorePercent: number;
  lowerPercent: number;
  upperPercent: number;
}

export interface StudentInsightsUIModel {
  courseCoverageCompletedTopics: number;
  courseCoverageTotalTopics: number;
  weakestSpots: StudentInsightsWeakestSpotUIModel[];
  topics: StudentInsightsTopicUIModel[];
  projectedScores: StudentInsightsProjectedScoreUIModel[];
}

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const toBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  return fallback;
};

const toString = (value: unknown, fallback = ''): string => {
  return typeof value === 'string' ? value : fallback;
};

const toObjectArray = (value: unknown): Array<Record<string, unknown>> => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (item): item is Record<string, unknown> => typeof item === 'object' && item !== null && !Array.isArray(item),
  );
};

export function mapStudentInsightsRpcRowToUIModel(row: StudentInsightsRpcRow | null): StudentInsightsUIModel {
  if (!row) {
    return {
      courseCoverageCompletedTopics: 0,
      courseCoverageTotalTopics: 0,
      weakestSpots: [],
      topics: [],
      projectedScores: [],
    };
  }

  return {
    courseCoverageCompletedTopics: toNumber(row.course_coverage_completed_topics),
    courseCoverageTotalTopics: toNumber(row.course_coverage_total_topics),
    weakestSpots: toObjectArray(row.weakest_spots).map((entry) => ({
      topicLevel02Id: toNumber(entry['topic_level_02_id']),
      topicCode: toString(entry['topic_code']),
      topicName: toString(entry['topic_name']),
      markImpact: toNumber(entry['mark_impact']),
      avgAccuracy: toNumber(entry['avg_accuracy']),
    })),
    topics: toObjectArray(row.topics).map((entry) => ({
      topicLevel02Id: toNumber(entry['topic_level_02_id']),
      topicCode: toString(entry['topic_code']),
      topicName: toString(entry['topic_name']),
      topicGroupName: toString(entry['topic_group_name']),
      avgMark: toNullableNumber(entry['avg_mark']),
      examWeight: toNumber(entry['exam_weight']),
      markImpact: toNullableNumber(entry['mark_impact']),
      avgAccuracy: toNullableNumber(entry['avg_accuracy']),
      lower: toNullableNumber(entry['lower']),
      upper: toNullableNumber(entry['upper']),
      isAssessed: toBoolean(entry['is_assessed']),
    })),
    projectedScores: toObjectArray(row.projected_scores).map((entry) => {
      const weekStartDate = toString(entry['week_start_date']) || toString(entry['date']);
      const scorePercent = toNumber(entry['score_percent']);
      const lowerPercent = toNullableNumber(entry['lower_percent']) ?? scorePercent;
      const upperPercent = toNullableNumber(entry['upper_percent']) ?? scorePercent;

      return {
        weekStartDate,
        scorePercent,
        lowerPercent,
        upperPercent,
      };
    }),
  };
}
